import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { POPUP } from 'utils/constants/popup';
import { useTranslation } from 'utils/modifiedTranslation';
import app from 'new-ui/app';
import noTitleLogoSrc from 'assets/logo_no_title.svg';
import logoSrc from 'assets/leal_logo_round.svg';
import { ReactComponent as UserIcon } from 'assets/icons/user_button.svg';
import { ReactComponent as BurgerIcon } from 'assets/icons/burger_menu.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close_menu.svg';
import NotePadIcon from 'assets/icons/note.svg';
import LanguageMenu from 'new-ui/Components/LanguageMenu';
import { ROUTE_NAME } from 'utils/constants/router';
import { LoginButton } from 'components/LoginButton/LoginButton';
import AiAssistant, { AI_WIDGET_THEMES } from 'new-ui/Components/AiAssistent';
import { HeaderTabs } from './HeaderTabs';
import { VERSIONS } from './constants';
import styles from './pageHeader.module.css';

const {
  DEFAULT: DEFAULT_VERSION,
  WHITE: WHITE_VERSION,
  HCP: HCP_VERSION,
} = VERSIONS;

const getHeaderVersionClassName = (version) => ({
  [DEFAULT_VERSION]: styles.defaultHeader,
  [WHITE_VERSION]: styles.whiteHeader,
  [HCP_VERSION]: styles.hcpHeader,
}[version]);

const adjustVersion = (rawVersion) => (
  rawVersion && Object.values(VERSIONS).includes(rawVersion)
    ? rawVersion
    : VERSIONS.DEFAULT
);

const renderLogo = ({ onLogoClick }) => (
  <>
    <img
      className={styles.logo}
      src={logoSrc}
      alt="Leal Health Logo"
      onClick={onLogoClick}
    />
    <img
      className={styles.logoNoTitle}
      src={noTitleLogoSrc}
      alt="Leal Health Logo"
      onClick={onLogoClick}
    />
  </>
);

const renderCustomLogo = (customLogoSrc, { onLogoClick }) => {
  try {
    return (
      <img
        src={customLogoSrc}
        onClick={onLogoClick}
        alt="logo"
        style={{ maxWidth: 180 }}
        className={styles.customLogo}
      />
    );
  } catch (e) {
  }
};

export const PageHeaderView = ({
  isProviderMode,
  isMobileMenuOpen = false,
  isProfileButtonShown,
  isUploadButtonShown,
  isLoginButtonShown,
  isLanguageMenuShown,
  onLogoClick,
  onHelpWithTinaClick,
  version: rawVersion,
  headerContent,
  toggleMobileMenu,
  isLogoShown,
  isAuthenticated = false,
  isBurgerMenuShown,
  customLogoScr,
  tabIds,
  isNavMenuShown,
  onMobileMenuItemClick,
  headerRightSlot,
}) => {
  const { t } = useTranslation();

  const version = adjustVersion(rawVersion);

  return (
    <div className={cx(styles.headerWrapper, { [styles.mobileMenuOpen]: isMobileMenuOpen })}>
      <header
        className={cx(
          styles.header,
          getHeaderVersionClassName(version),
          isProviderMode ? styles.providerHeader : styles.noProviderHeader,
        )}
      >
        {headerContent || (
          <>
            {/* leftSlot */}
            {(!isProviderMode && isLogoShown && (
              <div className={styles.logoContainer}>
                {
                  (customLogoScr && renderCustomLogo(customLogoScr, { onLogoClick }))
                  ?? renderLogo({ onLogoClick })
                }
              </div>
            )) || <div />}

            {/* middleSlot */}
            {isNavMenuShown && tabIds.length
              ? (
                <HeaderTabs
                  version={version}
                  tabIds={tabIds}
                  isMobileMenuOpen={isMobileMenuOpen}
                  onMenuItemClick={onMobileMenuItemClick}
                  isAuthenticated={isAuthenticated}
                />
              ) : null}

            {/* rightSlot */}
            {headerRightSlot || ((isProfileButtonShown || isUploadButtonShown) && (
              <div className={styles.buttonsWrapper}>
                {isUploadButtonShown && (
                  <div className={styles.uploadButton}>
                    <AiAssistant
                      theme={AI_WIDGET_THEMES.DARK}
                      customUploadIcon={NotePadIcon}
                      customUploadLabel={isMobileMenuOpen ? t('ai_profile.upload_button') : ' '}
                    />
                  </div>
                )}
                {isProfileButtonShown && (
                  <div className={styles.profileButton} onClick={() => { app.setPopup(POPUP.MEDICAL_PROFILE); }}>
                    <UserIcon />
                    <span className={styles.profileLabel}>{t('general.navbar.profile')}</span>
                  </div>
                )}
              </div>
            )) || ((isLanguageMenuShown || isLoginButtonShown) && (
              <div className={styles.rightCornerSlot}>
                {isLanguageMenuShown && <LanguageMenu />}
                {isLoginButtonShown && <LoginButton color={version === VERSIONS.DEFAULT ? 'primary' : undefined} />}
              </div>
            )) || <div />}

            {isBurgerMenuShown && (
              <div className={styles.burgerMenuWrapper}>
                {!app.isCurrentPage(ROUTE_NAME.CHAT) && (
                  <div
                    className={styles.helpButton}
                    onClick={onHelpWithTinaClick}
                  >
                    {t('general.help_with_tina')}
                  </div>
                )}

                <div className={styles.burger} onClick={toggleMobileMenu}>
                  {isMobileMenuOpen ? <CloseIcon /> : <BurgerIcon />}
                </div>
              </div>
            )}
          </>
        )}
      </header>
    </div>
  );
};

PageHeaderView.propTypes = {
  isAuthenticated: PropTypes.bool,
  isProviderMode: PropTypes.bool,
  isMobileMenuOpen: PropTypes.bool,
  isLogoShown: PropTypes.bool,
  isBurgerMenuShown: PropTypes.bool,
  isNavMenuShown: PropTypes.bool,
  isProfileButtonShown: PropTypes.bool,
  isUploadButtonShown: PropTypes.bool,
  isLoginButtonShown: PropTypes.bool,
  isLanguageMenuShown: PropTypes.bool,
  onLogoClick: PropTypes.func,
  onHelpWithTinaClick: PropTypes.func,
  toggleMobileMenu: PropTypes.func,
  version: PropTypes.string,
  headerContent: PropTypes.node,
  headerRightSlot: PropTypes.node,
  customLogoScr: PropTypes.string,
  tabIds: PropTypes.arrayOf(PropTypes.string),
  onMobileMenuItemClick: PropTypes.func,
};
